import { Controller } from "@hotwired/stimulus"
import { dia, shapes, V } from '@joint/plus'
import tippy from 'tippy.js'

// Wheel zoom sensitivity — higher = faster.
const WHEEL_ZOOM_SENSITIVITY = 0.0012
const PINCH_ZOOM_MULTIPLIER = 60
// Cap per-frame wheel delta so one mouse-notch does not jump.
const MAX_WHEEL_DELTA_PER_FRAME = 80
const ZOOM_VIEWPORT_SYNC_DELAY_MS = 100

// Connects to data-controller="network-map"
export default class extends Controller {
  static values = {
    network: Object,
    previewAssetPath: String,
    previewNetworkPath: String,
  }
  
  connect() {
    this.initializeProperties()
    this.initializePaper()
    this.setupEventListeners()
    this.draw()
  }

  disconnect() {
    this.hideNotSentTooltip()
    this.cancelHideNotSentTooltip()

    if (this.viewportSyncFrame) {
      cancelAnimationFrame(this.viewportSyncFrame)
      this.viewportSyncFrame = null
    }
    if (this.zoomFrame) {
      cancelAnimationFrame(this.zoomFrame)
      this.zoomFrame = null
    }
    if (this.zoomViewportSyncTimeout) {
      clearTimeout(this.zoomViewportSyncTimeout)
      this.zoomViewportSyncTimeout = null
    }

    document.body.style.cursor = ''
    this.parentContainer.style.cursor = ''

    document.removeEventListener('mousemove', this._onMouseMove)
    document.removeEventListener('mouseup',   this._onMouseUp)
    document.removeEventListener('touchmove', this._onTouchMove)
    document.removeEventListener('touchend',  this._onTouchEnd)
    window.removeEventListener('resize', this._onResize)

    this.parentContainer.removeEventListener('scroll', this._onScroll)
    document.removeEventListener('turbo:frame-load', this._onSidepanelFrameLoad)

    this.clearSidepanelLoading()
    // Clear so cancelPreviewPrefetch can abort an in-flight click fetch too.
    this.openingPreviewPath = null
    this.cancelPreviewPrefetch()
    this.cancelSidepanelLoadingOverlaySchedule()

    if (this.paper?.el) {
      this.paper.el.removeEventListener('touchstart', this._onTouchStart)
      this.paper.el.removeEventListener('wheel',      this._onWheel)
    }

    this.layoutObserver?.disconnect()
  }

  // ===== INITIALIZATION =====
  
  initializeProperties() {
    this.nodeSpacing = 100
    this.parentContainer = this.element
    this.graph = new dia.Graph()
    this.graphScale = 1
    this.renderedNodeIds = new Set()
    this.renderedLinkKeys = new Set()
    // linkKey → link cell. Scanning graph.getLinks() for an existing key made
    // every pan frame quadratic in link count.
    this.linkCells = new Map()
    this.nodeCells = new Map()
    // cell.id → nodeKey so icon/label resolve to the same group without guessing
    this.cellIdToNodeKey = new Map()
    
    // Touch interaction state
    this.isDragging = false
    this.isZooming = false
    this.lastTouchDistance = 0
    this.lastTouchCenter = { x: 0, y: 0 }
    this.lastZoomTime = 0
    
    // Tooltip state (unsynced / no-slug devices)
    this.currentTooltip = null
    this.tooltipNodeKey = null
    this.tooltipHideTimeout = null
    this.tooltipHideDelayMs = 300
    // Clicking an unsynced node fires a synthetic mouseleave in JointJS/SVG;
    // keep the tooltip up for the duration of that press.
    this.unsyncedPointerDownKey = null
    this.hoveredNodeKey = null
    // Own click detection — JointJS pointerclick drops presses with tiny movement
    // after hover attr updates, which felt like "click does nothing".
    this.pendingClickCellView = null
    this.pendingClickPoint = null
    this.clickMoveThresholdPx = 8

    // Preview prefetch (hover) cache
    this.previewCache = new Map()
    this.prefetchTimeout = null
    this.prefetchAbortController = null
    this.prefetchDelayMs = 100
    this.prefetchPath = null
    this.prefetchPromise = null
    this.openingPreviewPath = null
    this.previewOpenRequestId = 0
    this.sidepanelLoadingOverlayTimeout = null
    // Show the loading chrome on the same tick as the click so the panel never
    // feels like a no-op while Turbo fetches.
    this.sidepanelLoadingOverlayDelayMs = 0

    // Deferred fit when the container is not yet laid out (e.g. hidden turbo frame)
    this.pendingFitToView = false
    this.viewportSyncFrame = null
    this.contentTranslate = { x: 0, y: 0 }
  }

  initializePaper() {
    this.paper = new dia.Paper({
      el: this.parentContainer.firstElementChild,
      model: this.graph,
      gridSize: 16,
      drawGrid: true,
      interactive: false,
      async: true,
    })

    this.dropShadow = this.paper.defineFilter({
      name: 'dropShadow',
      args: {
        dx: 0,
        dy: 4,
        blur: 8,
        color: 'rgba(19, 1, 54, 0.16)'
      }
    })
  }

  setupEventListeners() {
    // Paper events — use pointerdown/up for opens; JointJS pointerclick is flaky
    // after hover style mutations (attr updates recreate SVG under the cursor).
    this.paper.on('cell:pointerdown', (cellView, evt) => this.cellPointerDown(cellView, evt))
    this.paper.on('cell:pointerup', (cellView, evt) => this.cellPointerUp(cellView, evt))
    this.paper.on('cell:mouseenter', (cellView, evt) => this.cellEnter(cellView, evt))
    this.paper.on('cell:mouseleave', (cellView, evt) => this.cellLeave(cellView, evt))
    this.paper.on('blank:pointerdown', (event) => this.pointerDown(event))
    this.paper.on('blank:pointerup', () => this.clearPendingCellClick())

    // Keep the link bookkeeping honest no matter who removes a cell, so a stale
    // key can never make drawLink skip a link that is no longer on the graph.
    this.graph.on('remove', (cell) => {
      const linkKey = cell.get('hudu_link_key')
      if (!linkKey) return

      this.linkCells.delete(linkKey)
      this.renderedLinkKeys.delete(linkKey)
    })

    // Store bound handlers so they can be removed on disconnect
    this._onMouseMove = (e) => this.pointerMove(e)
    this._onMouseUp   = ()  => this.pointerUp()
    this._onTouchStart = (e) => this.touchStart(e)
    this._onTouchMove  = (e) => this.touchMove(e)
    this._onTouchEnd   = (e) => this.touchEnd(e)
    this._onWheel      = (e) => this.scrollWheel(e)
    this._onResize     = ()  => {
      if (this.isDragging) return
      if (this.pendingFitToView && this.hasMeasurableContainer()) {
        this.fitToView()
      }
      this.syncViewport()
    }
    this._onScroll     = ()  => {
      if (this.isDragging || this.isZooming) return
      this.scheduleViewportSync()
    }

    document.addEventListener('mousemove', this._onMouseMove)
    document.addEventListener('mouseup',   this._onMouseUp)

    this.paper.el.addEventListener('touchstart', this._onTouchStart, { passive: false })
    document.addEventListener('touchmove', this._onTouchMove, { passive: false })
    document.addEventListener('touchend',  this._onTouchEnd,  { passive: false })

    // passive: false required so preventDefault() is honoured in Chrome
    this.paper.el.addEventListener('wheel', this._onWheel, { passive: false })
    window.addEventListener('resize', this._onResize)
    this.parentContainer.addEventListener('scroll', this._onScroll)

    this.parentContainer.style.cursor = 'grab'

    this.setupLayoutObserver()
    this._onSidepanelFrameLoad = (event) => {
      if (event.target.id === 'sidepanel') this.clearSidepanelLoading()
    }
    document.addEventListener('turbo:frame-load', this._onSidepanelFrameLoad)
  }

  hasMeasurableContainer() {
    return this.parentContainer.clientWidth > 0 && this.parentContainer.clientHeight > 0
  }

  setupLayoutObserver() {
    this.layoutObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect
        if (width > 0 && height > 0 && this.pendingFitToView) {
          this.fitToView()
          this.syncViewport()
          break
        }
      }
    })
    this.layoutObserver.observe(this.parentContainer)
  }

  getScrollOffset() {
    return {
      x: this.parentContainer.scrollLeft,
      y: this.parentContainer.scrollTop,
    }
  }

  // Extra scroll room so nodes can be panned into view when the sidepanel is open.
  // One viewport on each side — enough to clear the panel, not endless empty canvas.
  panMargin() {
    return {
      x: this.parentContainer.clientWidth,
      y: this.parentContainer.clientHeight,
    }
  }

  paperDimensionsForScale(scale) {
    const containerWidth = this.parentContainer.clientWidth
    const containerHeight = this.parentContainer.clientHeight
    const padding = this.nodeSpacing * 2
    const margin = this.panMargin()
    const contentWidth = (this.graphBBox.width + padding) * scale
    const contentHeight = (this.graphBBox.height + padding) * scale

    return {
      width: Math.max(containerWidth, contentWidth) + margin.x * 2,
      height: Math.max(containerHeight, contentHeight) + margin.y * 2,
    }
  }

  scheduleViewportSync() {
    if (this.viewportSyncFrame) return

    this.viewportSyncFrame = requestAnimationFrame(() => {
      this.viewportSyncFrame = null
      this.syncViewport()
    })
  }

  nodeKey(node) {
    if (node.id != null) return String(node.id)
    // Slug is unique per record; type:name collapses two synced nodes that share a name.
    if (node.slug) return `${node.type}:${node.slug}`
    if (node.deployment_name) return `${node.type}:${node.name}:${node.deployment_name}`
    return `${node.type}:${node.name}`
  }

  linkKey(sourceNode, targetNode) {
    return `${this.nodeKey(sourceNode)}->${this.nodeKey(targetNode)}`
  }

  registerNodeCells(node, cells) {
    const key = this.nodeKey(node)
    const list = cells.filter(Boolean)
    this.nodeCells.set(key, list)
    list.forEach((cell) => {
      if (cell?.id != null) this.cellIdToNodeKey.set(cell.id, key)
    })
    this.renderedNodeIds.add(key)
  }

  removeNodeByKey(key) {
    const cells = this.nodeCells.get(key) || []
    cells.forEach((cell) => {
      if (cell?.id != null) this.cellIdToNodeKey.delete(cell.id)
      cell.remove()
    })
    this.nodeCells.delete(key)
    this.renderedNodeIds.delete(key)
    // Links are coordinate-based and may still reach a visible neighbor — do not
    // remove them here. syncViewport prunes only when both ends are off-screen.
  }

  // ===== NODE INTERACTIONS =====
  
  // Handle click on network map nodes to open preview in sidepanel
  cellClick(cellView) {
    // Links have no preview — ignore them so a near-miss on a wire never looks
    // like a dead device click.
    if (cellView.model.isLink?.()) return

    const path = this.previewPathForCell(cellView)
    if (!path) {
      // Unsynced devices are not openable. Clicks must not dismiss the tooltip.
      this.keepUnsyncedTooltip(cellView)
      return
    }

    this.cancelPrefetchSchedule()
    this.abortInFlightPrefetch()
    return this.openSidepanelPreview(path)
  }

  cellPointerDown(cellView, evt) {
    if (cellView.model.isLink?.()) return
    if (!cellView.model.attributes.hudu_type) return

    this.pendingClickCellView = cellView
    this.pendingClickPoint = {
      x: evt?.clientX ?? evt?.originalEvent?.clientX ?? 0,
      y: evt?.clientY ?? evt?.originalEvent?.clientY ?? 0,
    }

    if (!cellView.model.attributes.hudu_slug) {
      this.unsyncedPointerDownKey = this.cellKeyFromModel(cellView.model)
      this.keepUnsyncedTooltip(cellView)
    }
  }

  cellPointerUp(cellView, evt) {
    const pending = this.pendingClickCellView
    const origin = this.pendingClickPoint
    this.clearPendingCellClick()

    if (!pending || !cellView) return
    if (pending.model !== cellView.model) return

    const x = evt?.clientX ?? evt?.originalEvent?.clientX ?? origin?.x ?? 0
    const y = evt?.clientY ?? evt?.originalEvent?.clientY ?? origin?.y ?? 0
    if (origin) {
      const dx = Math.abs(x - origin.x)
      const dy = Math.abs(y - origin.y)
      if (dx > this.clickMoveThresholdPx || dy > this.clickMoveThresholdPx) return
    }

    this.cellClick(cellView)
  }

  clearPendingCellClick() {
    this.pendingClickCellView = null
    this.pendingClickPoint = null
    this.unsyncedPointerDownKey = null
  }

  keepUnsyncedTooltip(cellView) {
    this.cancelHideNotSentTooltip()

    // Tippy's default hideOnClick can hide the instance while leaving our
    // reference set — re-show instead of assuming it's still visible.
    if (this.currentTooltip) {
      this.currentTooltip.show?.()
      return
    }

    this.showNotSentTooltip(cellView, !!cellView.model.getParentCell?.())
  }

  openSidepanelPreview(path) {
    // Each click gets an id so a slower earlier open cannot overwrite a newer one.
    const requestId = ++this.previewOpenRequestId
    this.openingPreviewPath = path

    // First open mounts #sidepanel. Later clicks target #sidepanel-content so the
    // shell stays is-visible (same pattern as preview prev/next) — visiting
    // #sidepanel remounts the modal and CSS slide-in runs again every time.
    const alreadyOpen = this.isSidepanelVisible()
    this.prepareSidepanelVisit({ alreadyOpen })

    if (this.previewCache.has(path) && this.renderCachedPreview(path, { alreadyOpen })) {
      this.finishPreviewOpen(path, requestId)
      return
    }

    // Do not await hover prefetch — that race caused "second click" opens when
    // the download was aborted or superseded. Navigate now; Turbo fills the frame.
    if (!this.isCurrentPreviewOpen(requestId)) return

    const frameId = alreadyOpen ? 'sidepanel-content' : 'sidepanel'
    const frame = document.getElementById(frameId)
    const sidepanelFrame = document.getElementById('sidepanel')
    if (sidepanelFrame) sidepanelFrame.dataset.sidepanelOpening = 'true'

    // Same-src Turbo.visit is a no-op on whichever frame we're targeting.
    if (frame?.getAttribute('src') === path) {
      frame.removeAttribute('complete')
      frame.removeAttribute('src')
    }

    this.finishPreviewOpen(path, requestId)
    Turbo.visit(path, { frame: frameId })
  }

  isSidepanelVisible() {
    // Frame is emptied on close; any sidepanel modal means we're swapping, not
    // opening cold (is-visible can lag a tick behind connect).
    const frame = document.getElementById('sidepanel')
    return !!frame?.querySelector('.manage_modal--sidepanel')
  }

  isCurrentPreviewOpen(requestId) {
    return requestId === this.previewOpenRequestId
  }

  finishPreviewOpen(path, requestId) {
    if (this.openingPreviewPath === path && this.isCurrentPreviewOpen(requestId)) {
      this.openingPreviewPath = null
    }
  }

  previewPathForCell(cellView) {
    const slug = cellView.model.attributes.hudu_slug
    if (!slug) return null

    const isNetwork = cellView.model.attributes.hudu_type === "network"
    return (isNetwork ? this.previewNetworkPathValue : this.previewAssetPathValue).replace('slug', slug)
  }

  schedulePreviewPrefetch(cellView) {
    const path = this.previewPathForCell(cellView)
    if (!path || this.previewCache.has(path)) return

    this.cancelPrefetchSchedule()
    this.prefetchTimeout = setTimeout(() => {
      this.prefetchPreview(path).catch(() => {})
    }, this.prefetchDelayMs)
  }

  cancelPrefetchSchedule() {
    if (this.prefetchTimeout) {
      clearTimeout(this.prefetchTimeout)
      this.prefetchTimeout = null
    }
  }

  cancelPreviewPrefetch() {
    this.cancelPrefetchSchedule()
    this.abortInFlightPrefetch()
  }

  abortInFlightPrefetch() {
    if (!this.prefetchAbortController) return

    this.prefetchAbortController.abort()
    this.prefetchAbortController = null
    this.prefetchPath = null
    this.prefetchPromise = null
  }

  prefetchPreview(path) {
    if (this.previewCache.has(path)) return Promise.resolve()

    if (this.prefetchPath === path && this.prefetchPromise) {
      return this.prefetchPromise
    }

    // Hover-only warm cache. Clicks navigate via Turbo.visit and do not await
    // this promise, so aborting on the next hover is safe.
    if (this.prefetchAbortController) {
      this.prefetchAbortController.abort()
      this.prefetchAbortController = null
    }

    this.prefetchPath = path
    const abortController = new AbortController()
    this.prefetchAbortController = abortController

    this.prefetchPromise = fetch(path, {
      signal: abortController.signal,
      credentials: 'same-origin',
      headers: {
        Accept: 'text/html, application/xhtml+xml',
        'Turbo-Frame': 'sidepanel',
        'X-Network-Map-Prefetch': '1',
      },
    }).then(async (response) => {
      if (response.ok) {
        this.previewCache.set(path, await response.text())
      }
    }).catch((error) => {
      if (error.name === 'AbortError') return
      console.debug('Network map preview prefetch failed', error)
    }).finally(() => {
      if (this.prefetchPath === path) {
        this.prefetchPath = null
        this.prefetchPromise = null
      }
      if (this.prefetchAbortController === abortController) {
        this.prefetchAbortController = null
      }
    })

    return this.prefetchPromise
  }

  renderCachedPreview(path, { alreadyOpen = false } = {}) {
    const html = this.previewCache.get(path)
    if (!html) return false

    const doc = new DOMParser().parseFromString(html, 'text/html')
    const fetchedFrame = doc.querySelector('turbo-frame#sidepanel')
    if (!fetchedFrame) return false

    if (alreadyOpen) {
      // Keep the open shell; only swap the inner content frame.
      const contentFrame = document.getElementById('sidepanel-content')
      const fetchedContent = fetchedFrame.querySelector('turbo-frame#sidepanel-content')
      if (!contentFrame || !fetchedContent) return false

      contentFrame.innerHTML = fetchedContent.innerHTML
      contentFrame.dispatchEvent(new CustomEvent('turbo:frame-load', { bubbles: true }))
    } else {
      const frame = document.getElementById('sidepanel')
      if (!frame) return false

      // Keep sidepanelOpening set across the HTML swap. turbo-modal reads it on
      // connect to skip the slide-in; clearing it here raced Stimulus connect and
      // caused intermittent re-animation when clicking between nodes.
      frame.dataset.sidepanelOpening = 'true'
      frame.innerHTML = fetchedFrame.innerHTML
      frame.dispatchEvent(new CustomEvent('turbo:frame-load', { bubbles: true }))
    }

    this.cancelSidepanelLoadingOverlaySchedule()
    this.removeSidepanelLoadingOverlay()
    this.recordPreviewOpen(path)
    return true
  }

  recordPreviewOpen(path) {
    fetch(path, {
      credentials: 'same-origin',
      headers: {
        Accept: 'text/html',
        'Turbo-Frame': 'sidepanel',
        'X-Network-Map-Log-Only': '1',
      },
    }).catch(() => {})
  }

  prepareSidepanelVisit({ alreadyOpen = false } = {}) {
    const frame = document.getElementById('sidepanel')
    if (frame) frame.dataset.sidepanelOpening = 'true'

    // Skip the loading overlay when swapping between devices — it made an
    // already-open panel look like it was closing and reopening.
    if (!alreadyOpen) {
      this.scheduleSidepanelLoadingOverlay()
    }
  }

  scheduleSidepanelLoadingOverlay() {
    this.cancelSidepanelLoadingOverlaySchedule()
    this.sidepanelLoadingOverlayTimeout = setTimeout(
      () => this.showSidepanelLoadingOverlay(),
      this.sidepanelLoadingOverlayDelayMs,
    )
  }

  cancelSidepanelLoadingOverlaySchedule() {
    if (this.sidepanelLoadingOverlayTimeout) {
      clearTimeout(this.sidepanelLoadingOverlayTimeout)
      this.sidepanelLoadingOverlayTimeout = null
    }
  }

  showSidepanelLoadingOverlay() {
    this.removeSidepanelLoadingOverlay()

    const overlay = document.createElement('div')
    overlay.className = 'network-map__sidepanel-loading-overlay manage_modal manage_modal--sidepanel is-visible'
    overlay.dataset.networkMapSidepanelLoading = 'true'
    overlay.innerHTML = `
      <header>
        <h1>Loading...</h1>
      </header>
      <div class="manage__modal__body">
        <i class="fas fa-spinner fa-spin" aria-hidden="true"></i>
      </div>
    `
    document.body.appendChild(overlay)
    this.sidepanelLoadingOverlay = overlay
  }

  removeSidepanelLoadingOverlay() {
    this.sidepanelLoadingOverlay?.remove()
    this.sidepanelLoadingOverlay = null
  }

  clearSidepanelLoading() {
    // Do not clear data-sidepanel-opening here — turbo-modal consumes it on
    // connect to skip slide-in. Clearing early caused intermittent re-animation.
    this.cancelSidepanelLoadingOverlaySchedule()
    this.removeSidepanelLoadingOverlay()
  }

  // Apply hover effects when mouse enters a node
  cellEnter(cellView, _evt) {
    const model = cellView.model
    if (model.isLink?.()) return

    const parentModel = model.getParentCell()
    const type = model.attributes.hudu_type
    const slug = model.attributes.hudu_slug

    if (!type) return

    if (slug) {
      this.hideNotSentTooltip()
      const nodeKey = this.cellKeyFromModel(model)
      // Re-applying attrs mid-hover recreates SVG nodes under the cursor and
      // breaks the following click. Only style when entering a new node.
      if (nodeKey !== this.hoveredNodeKey) {
        this.hoveredNodeKey = nodeKey
        this.applyHoverStyles(model, type)
      }
      this.schedulePreviewPrefetch(cellView)
      return
    }

    // Unsynced tooltip — keep one tippy alive across icon↔label sibling cells
    // (networks) and ignore leave→enter flicker on the same nodeKey.
    this.cancelHideNotSentTooltip()
    const nodeKey = this.cellKeyFromModel(model)
    if (nodeKey && this.tooltipNodeKey === nodeKey && this.currentTooltip) {
      this.updateNotSentTooltipPosition(cellView, !!parentModel)
      return
    }

    this.showNotSentTooltip(cellView, !!parentModel)
  }

  // Remove hover effects when mouse leaves a node
  cellLeave(cellView, evt) {
    const model = cellView.model
    if (model.isLink?.()) return

    const type = model.attributes.hudu_type

    if (!type) return

    const slug = model.attributes.hudu_slug

    // Device icon+name are one cell now, so leave means the cursor left the whole node.
    if (slug) {
      if (this.isPointerStillOnSameNode(cellView, evt)) return

      this.hoveredNodeKey = null
      this.clearHoverStyles(model, type)
      this.hideNotSentTooltip()
      this.cancelPreviewPrefetch()
      return
    }

    // Network nodes still use sibling cells; moving icon→label fires leave/enter.
    // Treat that as still-hovering so the tooltip does not blink off.
    if (this.isPointerStillOnSameNode(cellView, evt)) return

    const nodeKey = this.cellKeyFromModel(model)
    // Pressing on an unsynced node synthesizes a leave — do not hide mid-click.
    if (this.unsyncedPointerDownKey && this.unsyncedPointerDownKey === nodeKey) return

    this.scheduleHideNotSentTooltip(nodeKey)
  }

  isPointerStillOnSameNode(cellView, evt) {
    const nodeKey = this.cellKeyFromModel(cellView.model)
    if (!nodeKey || !evt?.relatedTarget || !this.paper) return false

    const nextView = this.paper.findView?.(evt.relatedTarget)
    if (!nextView?.model || nextView.model.isLink?.()) return false

    return this.cellKeyFromModel(nextView.model) === nodeKey
  }

  applyHoverStyles(model, type) {
    const hover = `#${this.getDeviceHoverColor(type)}`
    const filter = `url(#${this.dropShadow})`

    model.attr("body/filter", filter)
    model.attr("body/fill", hover)
    model.attr("body/stroke", hover)

    const attrs = model.get?.("attrs") || model.attributes?.attrs || {}
    if (attrs.nameBody) {
      model.attr("nameBody/filter", filter)
    }

    const embedded = model.getEmbeddedCells?.() || []
    embedded.forEach((cell) => cell.attr("body/filter", filter))
  }

  clearHoverStyles(model, type) {
    if (!model || !type) return

    const fill = `#${this.getDeviceColor(type)}`
    const stroke = `#${type === "network" ? this.getDeviceHoverColor(type) : this.getDeviceColor(type)}`

    model.attr("body/filter", null)
    model.attr("body/fill", fill)
    model.attr("body/stroke", stroke)

    const attrs = model.get?.("attrs") || model.attributes?.attrs || {}
    if (attrs.nameBody) {
      model.attr("nameBody/filter", null)
    }

    const embedded = model.getEmbeddedCells?.() || []
    embedded.forEach((cell) => cell.attr("body/filter", null))
  }

  cellKeyFromModel(model) {
    const key = model.attributes?.hudu_node_key
    if (key) return key

    const parentModel = model.getParentCell?.()
    if (parentModel?.attributes?.hudu_node_key) {
      return parentModel.attributes.hudu_node_key
    }

    const labelModel = model.getEmbeddedCells?.()[0]
    if (labelModel?.attributes?.hudu_node_key) {
      return labelModel.attributes.hudu_node_key
    }

    return null
  }

  scheduleHideNotSentTooltip(nodeKey) {
    this.cancelHideNotSentTooltip()

    this.tooltipHideTimeout = setTimeout(() => {
      this.tooltipHideTimeout = null
      if (nodeKey && this.tooltipNodeKey === nodeKey) {
        this.hideNotSentTooltip()
      }
    }, this.tooltipHideDelayMs)
  }

  cancelHideNotSentTooltip() {
    if (this.tooltipHideTimeout) {
      clearTimeout(this.tooltipHideTimeout)
      this.tooltipHideTimeout = null
    }
  }

  // ===== TOOLTIP METHODS =====

  notSentTooltipPagePoint(cellView, isLabelCell) {
    const model = cellView.model
    const bbox = model.getBBox()
    const scale = this.graphScale
    const centerGraphPoint = bbox.center()
    const offsetY = 10 / scale

    const aboveGraphPoint = centerGraphPoint.clone()
    aboveGraphPoint.y = bbox.y - offsetY + (isLabelCell ? -31 : 6)

    return this.paper.localToPagePoint(aboveGraphPoint)
  }

  updateNotSentTooltipPosition(cellView, isLabelCell) {
    if (!this.currentTooltip?.reference) return

    const pagePoint = this.notSentTooltipPagePoint(cellView, isLabelCell)
    const element = this.currentTooltip.reference
    element.style.left = `${pagePoint.x}px`
    element.style.top = `${pagePoint.y}px`
    this.currentTooltip.popperInstance?.update()
  }
  
  showNotSentTooltip(cellView, isLabelCell) {
    this.hideNotSentTooltip()
  
    const pagePoint = this.notSentTooltipPagePoint(cellView, isLabelCell)
  
    const tooltipElement = document.createElement('div')
    tooltipElement.style.position = 'absolute'
    tooltipElement.style.left = `${pagePoint.x}px`
    tooltipElement.style.top = `${pagePoint.y}px`
    tooltipElement.style.pointerEvents = 'none'
    tooltipElement.style.zIndex = '1000'
    document.body.appendChild(tooltipElement)
  
    const tooltipContent = 'Device not sent to Hudu'
    
    // hideOnClick false — tippy defaults to hiding on any click, which made the
    // "not sent" message vanish when pressing an unsynced device. duration 0
    // avoids fade flicker on leave/enter.
    this.currentTooltip = tippy(tooltipElement, {
      content: tooltipContent,
      theme: 'light',
      placement: 'top',
      arrow: true,
      interactive: false,
      trigger: 'manual',
      showOnCreate: true,
      hideOnClick: false,
      duration: 0,
      appendTo: () => document.body,
    })

    this.tooltipNodeKey = this.cellKeyFromModel(cellView.model)
  }
  
  hideNotSentTooltip() {
    this.cancelHideNotSentTooltip()
    this.tooltipNodeKey = null

    if (this.currentTooltip) {
      const element = this.currentTooltip.reference;
      this.currentTooltip.destroy();
      this.currentTooltip = null;
      
      // Remove the element from the DOM
      if (element && element.parentNode) {
        element.parentNode.removeChild(element);
      }
    }
  }

  // ===== MOUSE INTERACTIONS =====

  pointerDown(e) {
    this.isDragging = true
    this.startX = e.clientX
    this.startY = e.clientY
    this.scrollLeft = this.parentContainer.scrollLeft
    this.scrollTop = this.parentContainer.scrollTop

    this.parentContainer.style.cursor = 'grabbing'
    document.body.style.cursor = 'grabbing'
  }

  pointerMove(e) {
    if (!this.isDragging) return

    const x = e.clientX - this.startX
    const y = e.clientY - this.startY
    this.parentContainer.scrollLeft = this.scrollLeft - x
    this.parentContainer.scrollTop = this.scrollTop - y
    this.scheduleViewportSync()
  }

  pointerUp() {
    this.isDragging = false
    this.parentContainer.style.cursor = 'grab'
    document.body.style.cursor = ''
    this.syncViewport()
  }

  // ===== TOUCH INTERACTIONS =====
  
  // Handle touch start events for mobile panning and pinch-to-zoom
  touchStart(e) {
    if (e.touches.length === 1) {
      this.isDragging = true
      this.isZooming = false
      this.startX = e.touches[0].clientX
      this.startY = e.touches[0].clientY
      const offset = this.getScrollOffset()
      this.scrollLeft = offset.x
      this.scrollTop = offset.y
    } else if (e.touches.length === 2) {
      e.preventDefault()
      this.isDragging = false
      this.isZooming = true
      this.lastTouchDistance = this.getTouchDistance(e.touches)
      this.lastTouchCenter = this.getTouchCenter(e.touches)
    }
  }

  // Handle touch movement for panning and pinch-to-zoom gestures
  touchMove(e) {
    if (e.touches.length === 2) {
      e.preventDefault()
      
      if (!this.isZooming) {
        this.isDragging = false
        this.isZooming = true
        this.lastTouchDistance = this.getTouchDistance(e.touches)
        this.lastTouchCenter = this.getTouchCenter(e.touches)
        return
      }
      
      this.handlePinchZoom(e.touches)
    } else if (this.isDragging && e.touches.length === 1) {
      e.preventDefault()
      const x = e.touches[0].clientX - this.startX
      const y = e.touches[0].clientY - this.startY
      this.parentContainer.scrollLeft = this.scrollLeft - x
      this.parentContainer.scrollTop = this.scrollTop - y
      this.scheduleViewportSync()
    }
  }

  // Handle touch end events and state transitions
  touchEnd(e) {
    if (e.touches.length === 0) {
      this.isDragging = false
      this.isZooming = false
      this.syncViewport()
    } else if (e.touches.length === 1) {
      this.isZooming = false
      this.isDragging = true
      this.startX = e.touches[0].clientX
      this.startY = e.touches[0].clientY
      const offset = this.getScrollOffset()
      this.scrollLeft = offset.x
      this.scrollTop = offset.y
    }
  }

  // Calculate distance between two touch points for pinch gestures
  getTouchDistance(touches) {
    const dx = touches[0].clientX - touches[1].clientX
    const dy = touches[0].clientY - touches[1].clientY
    return Math.sqrt(dx * dx + dy * dy)
  }

  // Calculate center point between two touch points for zoom focus
  getTouchCenter(touches) {
    return {
      x: (touches[0].clientX + touches[1].clientX) / 2,
      y: (touches[0].clientY + touches[1].clientY) / 2
    }
  }

  // Handle pinch-to-zoom gesture
  handlePinchZoom(touches) {
    const currentDistance = this.getTouchDistance(touches)
    const currentCenter = this.getTouchCenter(touches)
    
    if (this.lastTouchDistance > 0) {
      const scale = currentDistance / this.lastTouchDistance
      const clampedScale = Math.max(0.5, Math.min(2.0, scale))
      const zoomFactor = (1 - clampedScale) * 0.5
      
      const currentTime = Date.now()
      const timeSinceLastZoom = currentTime - this.lastZoomTime
      
      if (Math.abs(zoomFactor) > 0.01 && timeSinceLastZoom > 16) {
        const syntheticEvent = {
          deltaY: zoomFactor * PINCH_ZOOM_MULTIPLIER,
          clientX: currentCenter.x,
          clientY: currentCenter.y,
          preventDefault: () => {}
        }
        
        this.applyZoom(syntheticEvent)
        this.lastZoomTime = currentTime
      }
    }
    
    this.lastTouchDistance = currentDistance
    this.lastTouchCenter = currentCenter
  }

  // ===== ZOOM FUNCTIONALITY =====

  // Handle mouse wheel and trackpad scroll/pinch events.
  // Heuristic to distinguish zoom vs pan (no keyboard modifier required):
  //   ctrlKey=true              → macOS trackpad pinch gesture          → zoom
  //   deltaMode !== 0           → line/page mode (physical mouse wheel) → zoom
  //   deltaX === 0 && |deltaY| >= 50 → large pure-vertical pixel delta  → zoom
  //   everything else           → trackpad two-finger scroll            → pan
  scrollWheel(e) {
    e.preventDefault()

    const isZoom = e.ctrlKey ||
                   e.deltaMode !== 0 ||
                   (e.deltaX === 0 && Math.abs(e.deltaY) >= 50)

    if (isZoom) {
      this.queueZoom(e)
    } else {
      this.parentContainer.scrollLeft += e.deltaX
      this.parentContainer.scrollTop += e.deltaY
      this.scheduleViewportSync()
    }
  }

  // Coalesce wheel events to one zoom step per animation frame for smoother feel.
  queueZoom(e) {
    this.pendingZoomDelta = (this.pendingZoomDelta || 0) + this.normalizedWheelDelta(e)
    this.pendingZoomPoint = { clientX: e.clientX, clientY: e.clientY }

    if (this.zoomFrame) return

    this.zoomFrame = requestAnimationFrame(() => {
      this.zoomFrame = null
      this.flushZoom()
    })
  }

  flushZoom() {
    const point = this.pendingZoomPoint
    let delta = this.pendingZoomDelta || 0
    this.pendingZoomDelta = 0
    this.pendingZoomPoint = null

    if (!point || delta === 0) return

    delta = Math.max(-MAX_WHEEL_DELTA_PER_FRAME, Math.min(MAX_WHEEL_DELTA_PER_FRAME, delta))
    this.applyZoom({ ...point, deltaY: delta, deltaMode: 0 })
  }

  // Apply a zoom step centred on the cursor/touch position
  applyZoom(e) {
    const oldScale = this.graphScale
    const newScale = this.clampScale(this.scaleFromWheelDelta(oldScale, e))

    if (newScale === oldScale) return

    this.graphScale = newScale
    this.scalePaper(e, oldScale)
    this.scheduleZoomViewportSync()
  }

  scheduleZoomViewportSync() {
    if (this.zoomViewportSyncTimeout) {
      clearTimeout(this.zoomViewportSyncTimeout)
    }

    // Defer culling until zooming settles — syncing every wheel tick feels bumpy.
    this.zoomViewportSyncTimeout = setTimeout(() => {
      this.zoomViewportSyncTimeout = null
      this.syncViewport()
    }, ZOOM_VIEWPORT_SYNC_DELAY_MS)
  }

  clampScale(scale) {
    return Math.min(2, Math.max(0.5, scale))
  }

  normalizedWheelDelta(e) {
    let delta = e.deltaY

    if (e.deltaMode === 1) {
      delta *= 16
    } else if (e.deltaMode === 2) {
      delta *= this.parentContainer.clientHeight || 600
    }

    return delta
  }

  scaleFromWheelDelta(currentScale, e) {
    const delta = this.normalizedWheelDelta(e)
    return currentScale * Math.exp(-delta * WHEEL_ZOOM_SENSITIVITY)
  }

  // Zoom in the network map (used by tests and pinch fallback)
  zoomIn(e) {
    this.applyZoom({ ...e, deltaY: e.deltaY ?? -33 })
  }

  // Zoom out the network map (used by tests and pinch fallback)
  zoomOut(e) {
    this.applyZoom({ ...e, deltaY: e.deltaY ?? 33 })
  }

  // Apply zoom scaling to the paper while keeping the cursor's graph point fixed
  // on screen (zoom-toward-cursor).
  scalePaper(e, oldScale) {
    this.isZooming = true

    const newScale = this.graphScale
    const { width, height } = this.paperDimensionsForScale(newScale)

    const containerRect = this.parentContainer.getBoundingClientRect()
    const offsetX = e.clientX - containerRect.left
    const offsetY = e.clientY - containerRect.top
    const paperX = this.parentContainer.scrollLeft + offsetX
    const paperY = this.parentContainer.scrollTop + offsetY
    const { x: translateX, y: translateY } = this.contentTranslate

    const graphX = (paperX - translateX) / oldScale
    const graphY = (paperY - translateY) / oldScale

    this.paper.scale(newScale, newScale)
    // paperDimensionsForScale already includes newScale — do not multiply again.
    this.paper.setDimensions(width, height)

    this.parentContainer.scrollLeft = (graphX * newScale) + translateX - offsetX
    this.parentContainer.scrollTop = (graphY * newScale) + translateY - offsetY

    requestAnimationFrame(() => {
      this.isZooming = false
    })
  }

  updatePaperDimensionsForScale() {
    this.sizeTree()
  }

  // ===== RENDERING =====

  // Initial draw of the network map
  draw() {
    this.sizeTree()
    // Fit first: sizing and centering read graphBBox (raw data), not the graph,
    // so there is no need to render the whole tree only to cull it a frame later.
    // Fitting up front also means the first sync uses the final scale/translate.
    this.fitToView()
    this.syncViewport()

    requestAnimationFrame(() => {
      if (!this.paper?.el) return
      this.syncViewport()
    })
  }

  // Backwards-compatible alias used by tests
  redraw() {
    this.syncViewport()
  }

  syncViewport({ renderAll = false } = {}) {
    if (!this.paper?.el || !this.graphBBox) return

    const viewport = renderAll ? this.getFullGraphViewport() : this.getViewportBounds()
    const shouldBeVisible = new Set()

    const walk = (node, parent) => {
      if (node.type === 'internet') {
        node.children?.forEach((child) => walk(child, node))
        return
      }

      const key = this.nodeKey(node)

      if (this.isBoxVisible(node, viewport)) {
        shouldBeVisible.add(key)

        if (!this.renderedNodeIds.has(key)) {
          if (node.type === 'network') {
            this.drawNetworkBox(node)
          } else {
            this.drawDeviceBox(node)
          }
        }
      }

      node.children?.forEach((child) => walk(child, node))
    }

    walk(this.networkValue, null)

    for (const key of [...this.renderedNodeIds]) {
      if (!shouldBeVisible.has(key)) {
        this.removeNodeByKey(key)
      }
    }

    // Drop edges only when both ends are off-screen; then restore any missing
    // edges that still touch a visible node (lines into/out of the viewport).
    this.pruneDetachedLinks(viewport)
    this.ensureVisibleLinks(viewport)
  }

  // A long trunk link can cross the viewport while both of its endpoints sit
  // off-screen, so visibility is decided by the link's own span rather than by
  // whether either end node is currently rendered.
  linkSpanVisible(source, target, viewport) {
    return Math.max(source.x, target.x) >= viewport.x1
      && Math.min(source.x, target.x) <= viewport.x2
      && Math.max(source.y, target.y) >= viewport.y1
      && Math.min(source.y, target.y) <= viewport.y2
  }

  pruneDetachedLinks(viewport = this.getViewportBounds()) {
    this.graph.getLinks().forEach((link) => {
      const linkKey = link.get('hudu_link_key')
      if (!linkKey) return

      const sep = linkKey.indexOf('->')
      if (sep < 0) return

      const sourceKey = linkKey.slice(0, sep)
      const targetKey = linkKey.slice(sep + 2)
      if (this.renderedNodeIds.has(sourceKey) || this.renderedNodeIds.has(targetKey)) return

      const source = link.get('source')
      const target = link.get('target')
      if (source && target && this.linkSpanVisible(source, target, viewport)) return

      link.remove()
      this.linkCells.delete(linkKey)
      this.renderedLinkKeys.delete(linkKey)
    })
  }

  ensureVisibleLinks(viewport = this.getViewportBounds()) {
    const walk = (node, parent) => {
      if (node.type === 'internet') {
        node.children?.forEach((child) => walk(child, node))
        return
      }

      const key = this.nodeKey(node)
      const nodeVisible = this.renderedNodeIds.has(key)
      const parentIsDevice = parent && parent.type !== 'internet'
      const parentVisible = parentIsDevice && this.renderedNodeIds.has(this.nodeKey(parent))
      // Also draw when the line itself crosses the viewport, even if neither
      // end node is on-screen.
      const spanVisible = parentIsDevice && this.linkSpanVisible(
        { x: parent.x + (parent.width / 2), y: parent.y + (parent.height / 2) },
        { x: node.x + (node.width / 2), y: node.y + (node.height / 2) },
        viewport,
      )

      // Draw if either end is on-screen so connectors aren't clipped mid-pan.
      if (parentIsDevice && (nodeVisible || parentVisible || spanVisible)) {
        this.drawLink(parent, node)
      }

      node.children?.forEach((child) => walk(child, node))
    }

    walk(this.networkValue, null)
  }

  // Calculate visible area bounds for viewport culling
  getViewportBounds() {
    const containerEl = this.paper.el.parentElement ?? this.parentContainer
    const rect = containerEl.getBoundingClientRect()

    if (rect.width <= 0 || rect.height <= 0) {
      return this.getFullGraphViewport()
    }

    const buffer = this.nodeSpacing * this.graphScale
    const topLeft = this.paper.clientToLocalPoint(rect.left - buffer, rect.top - buffer)
    const bottomRight = this.paper.clientToLocalPoint(rect.right + buffer, rect.bottom + buffer)

    return {
      x1: Math.min(topLeft.x, bottomRight.x),
      y1: Math.min(topLeft.y, bottomRight.y),
      x2: Math.max(topLeft.x, bottomRight.x),
      y2: Math.max(topLeft.y, bottomRight.y),
    }
  }

  getFullGraphViewport() {
    const padding = this.nodeSpacing

    return {
      x1: -padding,
      y1: -padding,
      x2: this.graphBBox.width + padding,
      y2: this.graphBBox.height + padding,
    }
  }

  // Check if a node is within the visible viewport bounds
  isBoxVisible(node, viewport) {
    const w = node.width
    const h = node.height

    return !(
      node.x + w < viewport.x1 ||
      node.x > viewport.x2 ||
      node.y + h < viewport.y1 ||
      node.y > viewport.y2
    )
  }

  // Set paper dimensions based on network tree size.
  // Bounding box is derived from raw data rather than graph.getBBox() because
  // viewport culling in syncViewport() skips off-screen nodes, causing getBBox() to
  // undercount the true extent of the graph.
  sizeTree() {
    this.graphBBox = this.getDataBBox(this.networkValue)
    const { width, height } = this.paperDimensionsForScale(1)
    this.paper.setDimensions(width, height)
  }

  // Fit the graph to the container — zoom out large maps, zoom in small maps.
  fitToView() {
    const padding = this.nodeSpacing * 2
    const contentWidth = this.graphBBox.width + padding
    const contentHeight = this.graphBBox.height + padding
    const containerWidth = this.parentContainer.clientWidth
    const containerHeight = this.parentContainer.clientHeight

    if (contentWidth <= 0 || contentHeight <= 0) return

    if (containerWidth <= 0 || containerHeight <= 0) {
      this.pendingFitToView = true
      return
    }

    this.pendingFitToView = false

    const scaleX = containerWidth / contentWidth
    const scaleY = containerHeight / contentHeight
    const idealScale = Math.min(scaleX, scaleY)

    const newScale = this.clampScale(idealScale)

    if (newScale !== this.graphScale) {
      this.graphScale = newScale
      this.paper.scale(newScale, newScale)
      const { width, height } = this.paperDimensionsForScale(newScale)
      this.paper.setDimensions(width, height)
    }

    this.centerGraphInView()
  }

  centerGraphInView() {
    const bbox = this.graphBBox
    const minX = bbox.minX ?? 0
    const minY = bbox.minY ?? 0
    const maxX = bbox.maxX ?? (minX + bbox.width)
    const containerWidth = this.parentContainer.clientWidth
    const scale = this.graphScale

    if (containerWidth <= 0 || this.parentContainer.clientHeight <= 0) return

    const margin = this.panMargin()
    const padding = this.nodeSpacing * 2
    const topPadding = this.nodeSpacing
    const nodeSpanX = (maxX - minX) * scale
    // Same middle band as paperDimensionsForScale — place the node span inside it so
    // left/right pan room stay equal (one viewport each). Old formula centered in the
    // viewport only, which pushed wide maps past scrollLeft 0 (CORE-9548).
    const middleWidth = Math.max(containerWidth, (bbox.width + padding) * scale)
    const nodesLeft = margin.x + (middleWidth - nodeSpanX) / 2
    const translateX = nodesLeft - (minX * scale)
    const translateY = margin.y + topPadding - (minY * scale)

    this.contentTranslate = { x: translateX, y: translateY }
    this.paper.translate(translateX, translateY)

    // Center the middle band in the viewport (small maps stay centered; wide maps
    // open on the middle of the graph with both edges still reachable).
    this.parentContainer.scrollLeft = margin.x + middleWidth / 2 - containerWidth / 2
    this.parentContainer.scrollTop = margin.y
  }

  // Recursively compute the bounding box of all nodes in the data tree,
  // regardless of whether they are currently rendered.
  getDataBBox(node, bounds = { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity }) {
    if (node.x != null && node.y != null) {
      bounds.minX = Math.min(bounds.minX, node.x)
      bounds.minY = Math.min(bounds.minY, node.y)
      bounds.maxX = Math.max(bounds.maxX, node.x + (node.width  || 0))
      bounds.maxY = Math.max(bounds.maxY, node.y + (node.height || 0))
    }

    if (node.children) {
      for (const child of node.children) {
        this.getDataBBox(child, bounds)
      }
    }

    if (bounds.minX === Infinity) {
      return { minX: 0, minY: 0, maxX: 0, maxY: 0, width: 0, height: 0 }
    }

    return {
      minX: bounds.minX,
      minY: bounds.minY,
      maxX: bounds.maxX,
      maxY: bounds.maxY,
      width: bounds.maxX - bounds.minX,
      height: bounds.maxY - bounds.minY,
    }
  }

  // ===== NODE DRAWING =====
  
  // Create a rectangle shape for network nodes
  drawRect(node) {
    return new shapes.standard.Rectangle({
      "hudu_type": node.type,
      "hudu_slug": node.slug,
      "hudu_node_key": this.nodeKey(node),
    })
  }

  // Draw device nodes as ONE cell (icon + name). JointJS mouseenter/leave are
  // per-cell, so a single cell means moving between icon and label never blinks.
  // hitArea covers the full bounds (including the visual gap) so empty space
  // between icon and name still counts as "over the node".
  drawDeviceBox(node) {
    const labelParts = this.labelLines(node)
    const labelText = labelParts.join('\n')
    const labelWidth = Math.max(...labelParts.map(part => this.measureText(this.paper.svg, part)))
    const labelHeight = labelParts.length > 1 ? (labelParts.length * 15) : 20
    const gap = 5
    const iconW = node.width
    const iconH = node.height
    const totalW = Math.max(iconW, labelWidth)
    const totalH = iconH + gap + labelHeight
    const bodyX = (totalW - iconW) / 2
    const nameX = (totalW - labelWidth) / 2

    const rect = new shapes.standard.Rectangle({
      hudu_type: node.type,
      hudu_slug: node.slug,
      hudu_node_key: this.nodeKey(node),
      markup: [{
        tagName: 'rect',
        selector: 'hitArea',
      }, {
        tagName: 'rect',
        selector: 'body',
      }, {
        tagName: 'text',
        selector: 'label',
      }, {
        tagName: 'rect',
        selector: 'nameBody',
      }, {
        tagName: 'text',
        selector: 'nameText',
      }],
    })

    rect.position(node.x + iconW / 2 - totalW / 2, node.y)
    rect.resize(totalW, totalH)
    rect.attr({
      root: node.slug ? { cursor: "pointer" } : { cursor: "default" },
      // Transparent but hittable — fill:"none" would let the cursor fall through the gap.
      hitArea: {
        fill: 'transparent',
        stroke: 'none',
        width: totalW,
        height: totalH,
        x: 0,
        y: 0,
      },
      body: {
        fill: `#${this.getDeviceColor(node.type)}`,
        stroke: `#${this.getDeviceColor(node.type)}`,
        rx: 12,
        ry: 12,
        width: iconW,
        height: iconH,
        x: bodyX,
        y: 0,
      },
      label: {
        text: this.getDeviceIcon(node.type),
        fontFamily: 'FontAwesome',
        fill: '#242424',
        fontSize: 18,
        x: bodyX + iconW / 2,
        y: iconH / 2,
        textAnchor: 'middle',
        textVerticalAnchor: 'middle',
      },
      nameBody: {
        fill: `#${this.getDeviceLabelColor(node.type)}`,
        stroke: `#${this.getDeviceLabelColor(node.type)}`,
        rx: 2,
        ry: 2,
        width: labelWidth,
        height: labelHeight,
        x: nameX,
        y: iconH + gap,
      },
      nameText: {
        text: labelText,
        fill: '#242424',
        fontSize: 12,
        x: nameX + labelWidth / 2,
        y: iconH + gap + labelHeight / 2,
        textAnchor: 'middle',
        textVerticalAnchor: 'middle',
      },
    })
    rect.addTo(this.graph)
    this.registerNodeCells(node, [rect])
  }

  // Draw network nodes with label and icon
  drawNetworkBox(node) {
    const rect = this.drawRect(node)
    const labelParts = this.labelLines(node)
    const labelText = labelParts.join('\n')
    const labelWidth = Math.max(...labelParts.map(part => this.measureText(this.paper.svg, part))) + 18
    const labelHeight = labelParts.length > 1 ? (labelParts.length * 15) + 4 : 22
    rect.position(node.x + node.width / 2 - labelWidth / 2, node.y)
    rect.resize(labelWidth, labelHeight)
    rect.attr({
      root: node.slug ? { cursor: "pointer" } : { cursor: "default" },
      body: { 
        fill: `#${this.getDeviceColor(node.type)}`,
        stroke: `#${this.getDeviceHoverColor(node.type)}`,
        strokeWidth: 1,
        rx: 2,
        ry: 2,
      },
      label: {
        text: labelText,
        fill: '#242424',
        fontSize: 12,
        x: labelWidth / 2 + 10
      }
    })
    rect.addTo(this.graph)
    
    const iconRect = new shapes.standard.Rectangle({
      "hudu_type": node.type,
      "hudu_slug": node.slug,
      "hudu_node_key": this.nodeKey(node),
    })
    iconRect.position(node.x + (node.width / 2 - labelWidth / 2) + 12, node.y + 11)
    iconRect.attr({
      root: node.slug ? { cursor: "pointer" } : { cursor: "default" },
      label: { 
        text: this.getDeviceIcon(node.type),
        fill: '#242424',
        fontSize: 14,
        fontFamily: 'FontAwesome'
      }
    })
    iconRect.addTo(this.graph)
    this.registerNodeCells(node, [rect, iconRect])
  }

  // Draw connection lines between network nodes
  drawLink(sourceNode, targetNode) {
    const linkKey = this.linkKey(sourceNode, targetNode)
    if (this.renderedLinkKeys.has(linkKey)) {
      // Guard against a stale key after a link was removed without clearing the set.
      if (this.linkCells.has(linkKey)) return
      this.renderedLinkKeys.delete(linkKey)
    }

    const link = new shapes.standard.Link({
      source: { x: sourceNode.x + (sourceNode.width / 2), y: sourceNode.y + (sourceNode.height / 2) },
      target: { x: targetNode.x + (targetNode.width / 2), y: targetNode.y + (targetNode.height / 2) }
    })
    
    link.router('orthogonal', { radius: 12 })
    link.connector('rounded')
    link.attr({
      line: {
        stroke: '#D1D1D1',
        strokeWidth: 1,
        sourceMarker: null,
        targetMarker: null,
      }
    })
    link.set('hudu_link_key', linkKey)
    link.addTo(this.graph)
    link.toBack()
    this.linkCells.set(linkKey, link)
    this.renderedLinkKeys.add(linkKey)
  }

  // Compose a node's display label lines. On the merged multi-collector map the
  // collector name arrives as its own field rather than baked into `name`, so it is
  // appended here. Splitting `name` is retained so maps from a hudu-services version
  // that still embeds the newline keep rendering correctly.
  labelLines(node) {
    const lines = String(node.name ?? '').split('\n')
    if (node.deployment_name) lines.push(`(${node.deployment_name})`)
    return lines
  }

  // Measure text width for label sizing
  measureText(svgDocument, text) {
    const vText = V('text').attr({fontSize: 12}).text(text)
    vText.appendTo(svgDocument)
    const bbox = vText.getBBox()
    vText.remove()
    return bbox.width + 8
  }

  // ===== STYLING =====
  
  // Get FontAwesome icon for device type
  getDeviceIcon(type) {
    const icons = {
      "network": "\uf6ff",
      "access_point": "\uf519",
      "firewall": "\ue3dc",
      "switch": "\uf796",
      "router": "\uf8da",
      "printer": "\uf02f",
      "laptop": "\uf109",
      "workstation": "\ue4e5",
      "server": "\uf233",
      "mobile": "\uf3ce",
      "network_device": "\uf5ef"
    }
    return icons[type]
  }

  // Get background color for device type
  getDeviceColor(type) {
    const colors = {
      "network": "EDE7F6BF",
      "access_point": "CE93D8",
      "firewall": "C5E1A5",
      "switch": "F48FB1",
      "router": "E6EE9C",
      "printer": "9FA8DA",
      "laptop": "A0D5D0",
      "workstation": "80DEEA",
      "server": "81D4FA",
      "mobile": "FFCC80",
      "network_device": "FFAB91"
    }
    return colors[type]
  }

  // Get label background color for device type
  getDeviceLabelColor(type) {
    const labelColors = {
      "network": "F0ECF7",
      "access_point": "F3E5F5BF",
      "firewall": "F1F8E9BF",
      "switch": "FCE4ECBF",
      "router": "F9FBE7BF",
      "printer": "E8EAF6BF",
      "laptop": "E0F2F1BF",
      "workstation": "E0F7FABF",
      "server": "E1F5FEBF",
      "mobile": "FFF3E0BF",
      "network_device": "FBE9E7BF"
    }
    return labelColors[type]
  }

  // Get hover color for device type
  getDeviceHoverColor(type) {
    const hoverColors = {
      "network": "B39DDB",
      "access_point": "BA68C8",
      "firewall": "AED581",
      "switch": "F06292",
      "router": "DCE775",
      "printer": "7986CB",
      "laptop": "80CBC4",
      "workstation": "4DD0E1",
      "server": "4FC3F7",
      "mobile": "FFB74D",
      "network_device": "FF8A65"
    }
    return hoverColors[type]
  }
}
